import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Waves, Anchor } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { session } = useAuth();

  useEffect(() => {
    // Redirect authenticated users to dashboard
    if (session) {
      navigate('/dashboard');
    }
  }, [session, navigate]);

  // Password visibility toggle logic
  useEffect(() => {
    const timeout = setTimeout(() => {
      const pwInput = document.querySelector('input[type="password"]');
      if (pwInput && !document.getElementById('orca-pw-toggle')) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.id = 'orca-pw-toggle';
        btn.style.position = 'absolute';
        btn.style.right = '1rem';
        btn.style.top = '50%';
        btn.style.transform = 'translateY(-50%)';
        btn.style.background = 'none';
        btn.style.border = 'none';
        btn.style.cursor = 'pointer';
        btn.style.display = 'flex';
        btn.style.alignItems = 'center';
        btn.style.justifyContent = 'center';
        btn.style.padding = '0';
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>`;
        btn.onclick = function() {
          if (pwInput.type === 'password') {
            pwInput.type = 'text';
          } else {
            pwInput.type = 'password';
          }
        };
        pwInput.parentNode.style.position = 'relative';
        pwInput.parentNode.appendChild(btn);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, []);

  // Add custom styles for the auth component
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .auth-container {
        color: white;
      }
      .auth-button {
        background: linear-gradient(to right, #10b981, #059669) !important;
        border: none !important;
        border-radius: 0.5rem !important;
      }
      .auth-input {
        background: rgba(255, 255, 255, 0.1) !important;
        border: 1px solid rgba(255, 255, 255, 0.2) !important;
        border-radius: 0.5rem !important;
        color: white !important;
      }
      .auth-input::placeholder {
        color: rgba(255, 255, 255, 0.6) !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="relative">
              <Waves className="h-12 w-12 text-emerald-400" />
              <Anchor className="h-6 w-6 text-white absolute -bottom-1 -right-1" />
            </div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">
              Orca AI
            </h1>
          </div>
          <p className="text-slate-300 text-sm">Yacht Charter Intelligence Dashboard</p>
        </div>

        {/* Auth Component - Shows both sign in and sign up */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl relative">
          <Auth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: '#10b981',
                    brandAccent: '#059669',
                    inputBackground: 'rgba(255, 255, 255, 0.1)',
                    inputText: 'white',
                    inputPlaceholder: 'rgba(255, 255, 255, 0.6)',
                  }
                }
              },
              className: {
                container: 'auth-container',
                button: 'auth-button',
                input: 'auth-input',
              }
            }}
            providers={[]}
            redirectTo={`${window.location.origin}/?reset-password=1`}
            onlyThirdPartyProviders={false}
            magicLink={false}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
